/**
 * Where the backend lives, resolved once for both the web and desktop builds.
 *
 * The web app is served from the same origin as its API (a Vite proxy in dev, a
 * reverse proxy in prod), so it talks to the backend over RELATIVE paths —
 * `/api/...` and `ws://<this host>/ws`. That is the historical behaviour and it
 * stays the default: when nothing overrides the origin, every helper here is a
 * no-op that yields the same relative URL the code used before.
 *
 * The Electron desktop build has no such same-origin server: its renderer loads
 * from a custom `app://` origin, where `/api` and `location.host` point at
 * nothing. The desktop main process therefore injects the real backend origin
 * (the locally-spawned server's loopback URL, or a configured hosted/LAN server)
 * on `window.__GENIE_BACKEND__` BEFORE the app boots, and every network call is
 * built through `apiUrl` / `wsUrl` so it reaches that origin instead.
 *
 * One resolver, two values — this is the whole of the "same code, both targets"
 * story. See packages/desktop/PLAN.md §2.
 */

declare global {
  // eslint-disable-next-line no-var
  var __GENIE_BACKEND__: string | undefined;
}

function resolveOrigin(): string {
  // 1. Desktop injects the absolute origin before the bundle runs.
  const injected = globalThis.__GENIE_BACKEND__;
  if (typeof injected === "string" && injected) return injected.replace(/\/+$/, "");
  // 2. An optional build-time override for the web target (e.g. a hosted API on
  //    a different origin). Absent in the normal same-origin web build.
  const fromEnv = import.meta.env.VITE_GENIE_BACKEND;
  if (typeof fromEnv === "string" && fromEnv) return fromEnv.replace(/\/+$/, "");
  // 3. Default: relative — unchanged web behaviour.
  return "";
}

/** The backend origin, or "" when the API is same-origin (the web default). */
export const BACKEND_ORIGIN = resolveOrigin();

/** An absolute API URL when an origin is configured; the relative path otherwise. */
export function apiUrl(path: string): string {
  return `${BACKEND_ORIGIN}${path}`;
}

/**
 * A WebSocket URL for a backend path. With a configured origin its scheme is
 * derived from that origin (http→ws, https→wss); without one it falls back to
 * the page's own host and protocol, exactly as the sockets did before.
 */
export function wsUrl(path: string): string {
  if (BACKEND_ORIGIN) return `${BACKEND_ORIGIN.replace(/^http/, "ws")}${path}`;
  const protocol = location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${location.host}${path}`;
}
